import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldAlert, Check } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { renderAnnouncementMarkdown } from './announcementMarkdown'
import type { Announcement } from './types'
import type { SnowflakeId } from '@/types/snowflake'

function pick(base: string | null | undefined, i18n: Record<string, string> | undefined, lng: string): string {
  return (i18n && i18n[lng]) || base || ''
}

interface Props {
  /** The mandatory-acknowledge announcement to block on, or null when none pending. */
  ann: Announcement | null
  onAcknowledge: (id: SnowflakeId) => void
}

/**
 * Blocking acknowledgement dialog (scenario 17). Non-dismissable: no ×, no snooze,
 * Esc / outside-click don't close. The confirm button enables only after the user
 * scrolls the content to the end. Re-surfaces every session until acknowledged.
 */
export function AnnouncementAckDialog({ ann, onAcknowledge }: Props) {
  const { t, i18n } = useTranslation('announcements')
  const lng = i18n.language?.split('-')[0] ?? 'en'
  const bodyRef = useRef<HTMLDivElement>(null)
  const [readToEnd, setReadToEnd] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Reset the gate when the announcement changes, then poll the layout: once the
  // dialog has finished animating in (clientHeight > 0), if the content isn't tall
  // enough to scroll there's nothing to read past — enable confirm. Scrollable
  // content stays gated until onScroll reaches the bottom.
  useEffect(() => {
    setReadToEnd(false)
    if (!ann) return
    let tries = 0
    let tid: ReturnType<typeof setTimeout>
    const check = () => {
      const el = bodyRef.current
      if (el && el.clientHeight > 0) {
        if (el.scrollHeight <= el.clientHeight + 4) { setReadToEnd(true); return }
        return // scrollable → wait for the user to scroll
      }
      if (tries++ < 20) tid = setTimeout(check, 150) // not laid out yet (animating)
    }
    tid = setTimeout(check, 150)
    return () => clearTimeout(tid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ann?.id])

  if (!ann) return null

  const onScroll = () => {
    const el = bodyRef.current
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setReadToEnd(true)
  }

  const confirm = async () => {
    setSubmitting(true)
    try {
      await Promise.resolve(onAcknowledge(ann.id))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open>
      <DialogContent
        hideClose
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="max-w-2xl"
      >
        <div className="mb-1 flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-lg bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-300">
            <ShieldAlert size={16} />
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400">{t('ackKicker')}</span>
        </div>
        <h2 className="text-xl font-bold">{pick(ann.title, ann.titleI18n, lng)}</h2>

        <div
          ref={bodyRef}
          onScroll={onScroll}
          className="prose prose-sm mt-2 max-h-[55vh] max-w-none overflow-auto"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: renderAnnouncementMarkdown(pick(ann.content, ann.contentI18n, lng)) }}
        />

        <div className="mt-3 flex items-center justify-between gap-3 border-t pt-3">
          <span className="text-xs text-muted-foreground">
            {readToEnd ? t('ackReadyHint') : t('ackScrollHint')}
          </span>
          <Button onClick={confirm} disabled={!readToEnd || submitting} className="gap-1.5">
            <Check size={15} /> {t('ackConfirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
